import clsx from "clsx";
import dayjs from "dayjs";
import { useEffect, useState } from "react";

import { DutyStatus } from "@web-app/types";

import DutyStatusIcon from "./DutyStatusIcon";
import { Icon } from "./Icon";

type AvatarType = {
  dutyStatus?: DutyStatus;
  image?: string;
  lastSeen?: string;
  name?: string;
  onImageError?: () => void;
  onImageLoad?: () => void;
  size: number;
  style?: "circle" | "square";
};

export default function Avatar({
  image,
  name,
  onImageError,
  onImageLoad,
  size = 10,
  style = "circle",
  dutyStatus,
  lastSeen,
}: AvatarType) {
  const [imageError, setImageError] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(Boolean(image));

  const initials = name
    ?.split(" ")
    .splice(0, 2)
    .map((n) => n[0])
    .join("");

  const handleImageError = () => {
    setImageError(true);
    setIsImageLoading(false);
    onImageError?.();
  };

  const handleImageLoad = () => {
    setImageError(false);
    setIsImageLoading(false);
    onImageLoad?.();
  };

  const getLastSeenMinutes = () => {
    if (!lastSeen) return null;
    return dayjs().diff(dayjs(lastSeen), "minute");
  };

  const isActive = () => {
    const minutes = getLastSeenMinutes();
    return minutes === null || minutes <= 1;
  };

  const sizeClasses = {
    4: "h-4 w-4",
    6: "h-6 w-6",
    8: "h-6 lg:h-8 w-6 lg:w-8",
    10: "h-7 lg:h-10 w-7 lg:w-10",
    12: "h-8 lg:h-12 w-8 lg:w-12",
    14: "h-10 lg:h-14 w-10 lg:w-14",
    16: "h-12 lg:h-16 w-12 lg:w-16",
    20: "h-16 lg:h-20 w-16 lg:w-20",
    28: "h-28 w-28",
    32: "h-32 w-32",
    40: "h-40 w-40",
  };

  useEffect(() => {
    setImageError(false);
    setIsImageLoading(Boolean(image));
  }, [image]);

  const renderDutyStatus = () => {
    if (!dutyStatus) return null;

    const minutes = getLastSeenMinutes();
    const isUserActive = isActive();

    return (
      <div
        className={clsx(
          "absolute bottom-0 right-0",
          "flex items-center justify-center",
          size <= 20 ? "h-2.5 w-2.5" : "h-4 w-4",
          !isUserActive && "bg-gray-100 rounded-full",
        )}
      >
        {isUserActive ? (
          <DutyStatusIcon
            status={dutyStatus}
            className={clsx(size <= 20 ? "h-2.5 w-2.5" : "h-4 w-4")}
          />
        ) : (
          <span
            className={clsx(
              "text-[8px] font-medium text-gray-600 bg-green-100 rounded-full p-1",
              size <= 20 ? "text-[6px]" : "text-[8px]",
            )}
          >
            {minutes}m
          </span>
        )}
      </div>
    );
  };

  if (image && !imageError) {
    return (
      <span
        className={clsx(
          "inline-block relative",
          sizeClasses[size as keyof typeof sizeClasses] || "h-10 w-10",
        )}
      >
        {isImageLoading && (
          <div
            aria-label="Loading avatar"
            className={clsx(
              "absolute inset-0 z-10 flex items-center justify-center border border-gray-200",
              "bg-gray-100 dark:bg-gray-600",
              style == "circle" ? "rounded-full" : "",
            )}
            role="status"
          >
            <Icon
              name="circleNotch"
              className="h-1/3 w-1/3 animate-spin text-gray-200"
            />
          </div>
        )}
        <img
          className={clsx(
            sizeClasses[size as keyof typeof sizeClasses] || "h-10 w-10",
            "inline-block object-cover border-gray-300 transition-opacity duration-200",
            isImageLoading ? "opacity-0" : "opacity-100",
            style == "circle" ? "rounded-full" : "",
          )}
          src={image}
          alt={name || "User avatar"}
          onError={handleImageError}
          loading="lazy"
          crossOrigin="anonymous"
          onLoad={handleImageLoad}
        />
        {renderDutyStatus()}
      </span>
    );
  }

  return (
    <div
      className={clsx(
        sizeClasses[size as keyof typeof sizeClasses] || "h-10 w-10",
        "relative inline-flex items-center justify-center",
        "bg-gray-200 dark:bg-gray-600",
        "border-[1px] border-gray-200",
        "transition-all duration-200",
        style == "circle" ? "rounded-full" : "",
      )}
    >
      <span
        className={clsx(
          "font-medium text-gray-600 dark:text-gray-300",
          size <= 20 ? "text-xs" : "text-4xl",
        )}
      >
        {initials || (
          <Icon name="user" className="h-4 w-4" data-testid="user-icon" />
        )}
      </span>
      {renderDutyStatus()}
    </div>
  );
}
